import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { 
    getModelStructure, 
    toggleItemsVisibility, 
    toggleItemVisibilityByUuid
} from 'Domain/Scene/Handler/ModelVisibilityHandler'

/**
 * ModelSelector - Composant générique pour afficher/masquer des éléments 3D
 * Fonctionne avec n'importe quelle structure retournée par getDentalModelStructure
 */
export const ModelSelector = () => {
    const [structure, setStructure] = useState(null)
    const [expandedNodes, setExpandedNodes] = useState({})
    // updateTick force le re-render quand la visibilité change
    const [, setUpdateTick] = useState(0)
    
    const sceneLoaded = useSelector(state => state.AnimationReducer.loaded)
    const keyframe = useSelector(state => state.AnimationReducer.keyframe)

    useEffect(() => {
        if (sceneLoaded) {
            refreshTree()
        }
    }, [sceneLoaded])

    useEffect(() => {
        setUpdateTick(n => n + 1)
    }, [keyframe])

    const refreshTree = () => {
        const data = getModelStructure()
        setStructure(data)
        // Ouvrir les nœuds racines par défaut
        if (data) {
            const expanded = {}
            Object.keys(data).forEach(key => expanded[key] = true)
            setExpandedNodes(expanded)
        }
    }

    const toggleExpand = (e, key) => {
        e.stopPropagation()
        if (e.preventDefault) e.preventDefault()
        setExpandedNodes(prev => ({ ...prev, [key]: !prev[key] }))
    }

    // Toggle visibility pour un groupe d'items
    const toggleGroupVisibility = (items) => {
        if (!items || items.length === 0) return
        const hasVisible = items.some(item => item.visible)
        const uuids = items.map(item => item.uuid)
        toggleItemsVisibility(uuids, !hasVisible)
        // Mettre à jour les données locales
        items.forEach(item => {
            item.visible = !hasVisible
        })
        setUpdateTick(n => n + 1)
    }

    // Toggle visibility pour un item individuel
    const toggleItemVisibility = (item) => {
        toggleItemVisibilityByUuid(item.uuid, !item.visible)
        item.visible = !item.visible
        setUpdateTick(n => n + 1)
    }

    // Toggle visibility pour un nœud parent (tous ses enfants)
    const toggleNodeVisibility = (node) => {
        const allItems = collectAllItems(node)
        if (allItems.length === 0) return
        const hasVisible = allItems.some(item => item.visible)
        const uuids = allItems.map(item => item.uuid)
        toggleItemsVisibility(uuids, !hasVisible)
        // Mettre à jour les données locales
        allItems.forEach(item => {
            item.visible = !hasVisible
        })
        setUpdateTick(n => n + 1)
    }

    // Collecter tous les items d'un nœud récursivement
    const collectAllItems = (node) => {
        let items = []
        if (node.items) items = [...node.items]
        if (node.children) {
            Object.values(node.children).forEach(child => {
                items = [...items, ...collectAllItems(child)]
            })
        }
        return items
    }

    // Vérifier si un groupe est visible
    const isGroupVisible = (items) => {
        if (!items || items.length === 0) return false
        return items.some(item => item.visible)
    }

    // Vérifier si au moins un item d'un nœud est visible
    const isNodeVisible = (node) => {
        const allItems = collectAllItems(node)
        if (allItems.length === 0) return true
        return allItems.some(item => item.visible)
    }

    // Rendu d'un item individuel
    const renderItem = (item, index) => {
        const isVisible = item.visible
        return (
            <div 
                key={item.uuid || index}
                className={`model-selector-row item ${!isVisible ? 'row-hidden' : ''}`}
                onClick={() => toggleItemVisibility(item)}
            >
                <span className="expand-placeholder"></span>
                <span className={`node-name ${!isVisible ? 'node-hidden' : ''}`}>
                    {item.name || `Item ${index + 1}`}
                </span>
            </div>
        )
    }

    // Rendu d'un groupe enfant (ex: Dents, Gencive)
    const renderChildGroup = (childKey, child, parentKey) => {
        const expandKey = `${parentKey}-${childKey}`
        const isExpanded = expandedNodes[expandKey]
        const isVisible = isGroupVisible(child.items)
        const hasItems = child.items && child.items.length > 0
        const canExpand = child.expandable && hasItems

        return (
            <li key={childKey} className="model-selector-node">
                <div 
                    className={`model-selector-row group ${!isVisible ? 'row-hidden' : ''}`}
                    onClick={() => toggleGroupVisibility(child.items)}
                >
                    {canExpand ? (
                        <button
                            type="button"
                            className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Réduire' : 'Développer'}
                            onClick={(e) => toggleExpand(e, expandKey)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space') {
                                    e.preventDefault()
                                    toggleExpand(e, expandKey)
                                }
                            }}
                        >▶</button>
                    ) : (
                        <span className="expand-placeholder"></span>
                    )}
                    <span className={`node-name ${!isVisible ? 'node-hidden' : ''}`}>
                        {child.name}
                    </span>
                    {hasItems && <span className="item-count">({child.items.length})</span>}
                </div>
                
                {canExpand && isExpanded && (
                    <ul className="model-selector-children">
                        {child.items.map((item, i) => renderItem(item, i))}
                    </ul>
                )}
            </li>
        )
    }

    // Rendu d'un nœud racine (ex: Maxillaire, Mandibulaire)
    const renderRootNode = (nodeKey, node) => {
        const isExpanded = expandedNodes[nodeKey]
        const nodeVisible = isNodeVisible(node)
        const hasChildren = node.children && Object.keys(node.children).length > 0
        const hasItems = node.items && node.items.length > 0

        return (
            <li key={nodeKey} className="model-selector-node">
                <div 
                    className={`model-selector-row arch ${!nodeVisible ? 'row-hidden' : ''}`}
                    onClick={() => toggleNodeVisibility(node)}
                >
                    {(hasChildren || hasItems) ? (
                        <button
                            type="button"
                            className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Réduire' : 'Développer'}
                            onClick={(e) => toggleExpand(e, nodeKey)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space') {
                                    e.preventDefault()
                                    toggleExpand(e, nodeKey)
                                }
                            }}
                        >▶</button>
                    ) : (
                        <span className="expand-placeholder"></span>
                    )}
                    <span className={`node-name ${!nodeVisible ? 'node-hidden' : ''}`}>
                        {node.name}
                    </span>
                </div>
                
                {isExpanded && (
                    <ul className="model-selector-children">
                        {/* Items directs du nœud */}
                        {hasItems && node.items.map((item, i) => renderItem(item, i))}
                        
                        {/* Groupes enfants */}
                        {hasChildren && Object.entries(node.children).map(([childKey, child]) => 
                            renderChildGroup(childKey, child, nodeKey)
                        )}
                    </ul>
                )}
            </li>
        )
    }

    if (!structure) {
        return (
            <div className="model-selector">
                <div className="model-selector-header">
                    <h4>Modèle 3D</h4>
                </div>
                <div className="model-selector-empty">Aucun modèle chargé</div>
            </div>
        )
    }

    return (
        <div className="model-selector">
            <div className="model-selector-header">
                <h4>Modèle 3D</h4>
                <button className="refresh-btn" onClick={refreshTree}>↻</button>
            </div>
            <ul className="model-selector-tree">
                {Object.entries(structure).map(([nodeKey, node]) => 
                    renderRootNode(nodeKey, node)
                )}
            </ul>
        </div>
    )
}
